const API_HOST = 'https://api.softgarden.io';

export class SoftgardenApiError extends Error {
	constructor(message, { status = 0, code = '', body = null } = {}) {
		super(message);
		this.name = 'SoftgardenApiError';
		this.status = status;
		this.code = code;
		this.body = body;
	}
}

/** Debug via URL: ?sgDebug=1 (oder nur ?sgDebug) */
export const isSoftgardenDebug = () => {
	try {
		return new URLSearchParams(window.location.search).has('sgDebug');
	} catch {
		return false;
	}
};

export const logSoftgardenDebug = (label, payload) => {
	if (!isSoftgardenDebug()) return;
	console.warn(`[softgarden] ${label}`, payload);
};

export const createApiClient = (root) => {
	const clientId = root.dataset.clientId || '';
	const proxyUrl = root.dataset.proxyUrl || '';
	const useProxy = Boolean(proxyUrl);

	const client = {
		clientId,
		useProxy,
		userToken: null,
	};

	const buildUrl = (path, query = {}) => {
		const normalizedPath = path.startsWith('/') ? path : `/${path}`;

		if (useProxy) {
			const url = new URL(proxyUrl, window.location.origin);
			url.searchParams.set('path', normalizedPath);
			Object.entries(query).forEach(([key, value]) => {
				url.searchParams.set(key, value);
			});
			return url.toString();
		}

		const url = new URL(`${API_HOST}${normalizedPath}`);
		Object.entries(query).forEach(([key, value]) => {
			url.searchParams.set(key, value);
		});
		return url.toString();
	};

	const parseResponse = async (response) => {
		const text = await response.text();
		let data = text;

		if (text) {
			try {
				data = JSON.parse(text);
			} catch {
				data = text;
			}
		}

		if (!response.ok) {
			let code = '';
			let message = '';

			if (typeof data === 'object' && data !== null) {
				code = data.code || data.error || '';
				message = data.message || data.error_description || '';
			} else if (typeof data === 'string' && data.trim() !== '') {
				const trimmed = data.trim();
				code = trimmed;
				message = trimmed;
			}

			if (!message) {
				message = response.statusText;
			}

			logSoftgardenDebug('API error', {
				url: response.url,
				status: response.status,
				code: String(code),
				message: message || 'API request failed',
				body: data,
			});

			throw new SoftgardenApiError(message || 'API request failed', {
				status: response.status,
				code: String(code),
				body: data,
			});
		}

		return data;
	};

	const request = async (path, {
		method = 'GET',
		query = {},
		headers = {},
		body = undefined,
		auth = 'client',
	} = {}) => {
		const requestHeaders = {
			Accept: 'application/json',
			...headers,
		};

		if (auth === 'user') {
			if (!client.userToken) {
				throw new SoftgardenApiError('Bewerber-Session fehlt. Bitte erneut versuchen.', { status: 401 });
			}
			requestHeaders.Authorization = `Bearer ${client.userToken}`;
		} else if (auth === 'client' && clientId) {
			requestHeaders.Authorization = `Basic ${btoa(`${clientId}:`)}`;
		}

		let payload = body;

		if (payload instanceof FormData) {
			// Boundary vom Browser setzen lassen
			delete requestHeaders['Content-Type'];
		} else if (payload instanceof URLSearchParams) {
			requestHeaders['Content-Type'] = requestHeaders['Content-Type'] || 'application/x-www-form-urlencoded';
			payload = payload.toString();
		} else {
			// Softgarden erwartet application/json auch bei GET (vgl. Dev-Docs)
			requestHeaders['Content-Type'] = requestHeaders['Content-Type'] || 'application/json';

			if (payload !== undefined && payload !== null) {
				payload = JSON.stringify(payload);
			} else if (method !== 'GET' && method !== 'HEAD') {
				payload = '';
			}
		}

		const response = await fetch(buildUrl(path, query), {
			method,
			headers: requestHeaders,
			body: method === 'GET' || method === 'HEAD' ? undefined : payload,
		});

		return parseResponse(response);
	};

	client.get = (path, options = {}) => request(path, { ...options, method: 'GET' });
	client.post = (path, body, options = {}) => request(path, { ...options, method: 'POST', body });
	client.put = (path, body, options = {}) => request(path, { ...options, method: 'PUT', body });
	client.postForm = (path, formData, options = {}) => request(path, {
		...options,
		method: 'POST',
		body: formData,
	});

	client.requestToken = async (username, password) => {
		const params = new URLSearchParams({
			grant_type: 'password',
			username,
			password,
		});

		if (useProxy) {
			const url = buildUrl('/api/rest/oauth/frontend/token');
			const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
			if (clientId) {
				headers.Authorization = `Basic ${btoa(`${clientId}:`)}`;
			}
			const response = await fetch(url, { method: 'POST', headers, body: params });
			return parseResponse(response);
		}

		const response = await fetch(`${API_HOST}/api/rest/oauth/frontend/token`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				Authorization: `Basic ${btoa(`${clientId}:`)}`,
			},
			body: params,
		});

		return parseResponse(response);
	};

	/** Softgarden erwartet form-urlencoded: authToken + password */
	client.resetPassword = (authToken, password) => {
		const params = new URLSearchParams({
			authToken,
			password,
		});

		return request('/api/rest/v3/frontend/resetPassword', {
			method: 'POST',
			body: params,
			auth: 'client',
		});
	};

	return client;
};
